import { FC, useMemo, useEffect, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { AddIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { batchAnalysts, deleteAnalyst, getAnalystById, getAnalysts, newAnalyst, updateAnalyst } from "@/services/analysts-service";
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  useDisclosure,
  useToast,
  VStack,
  Input as ChakraInput,
  Center,
  Spinner,
} from "@chakra-ui/react";

import { Table, TableRowActions, Panel, PhoneInput, PageHeading } from "@bemap/ui";
import { CreditPayload, formatDate, ITeam, renderTier, renderTierBadge } from "@bemap/utils";
import { resetPassword } from "../reset-password/reset-password-service";
import { handleCredit } from "@/services/partners-service";
import { MdList } from "react-icons/md";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

const AnalystsPage: FC = () => {
  const { t } = useTranslation();
  const toast = useToast()
  const navigate = useNavigate()
  const initialAnalyst: ITeam = {
    id: 0,
    name: '',
    userId: 0,
    personalTeam: false,
    isPremium: false,
    type: "",
    profilePhotoPath: "",
    user: {
      id: 0,
      name: '',
      email: '',
      phone: '',
      password: '',
      totalCredits: 0,
    }
  }

  const initialCredit: CreditPayload = {
    amount: 0,
    by_user_id: 0
  }

  // STATES
  const cancelRef = useRef<HTMLButtonElement>(null)
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [modalType, setModalType] = useState<"edit" | "new" | "delete" | "transfer" | "retrieve" | "batch" | "reset_password">();
  const [analysts, setAnalysts] = useState<ITeam[]>([]);
  const [analyst, setAnalyst] = useState<ITeam>(initialAnalyst);
  const [credits, setCredits] = useState<CreditPayload>(initialCredit);
  const [updateData, setUpdateData] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);

  const columns = useMemo<ColumnDef<ITeam, any>[]>(
    () => [
      {
        accessorFn: (row) => row.name,
        id: "Nome",
        header: t('partner.analysts_table_name'),
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => renderTier(row.isPremium ?? false),
        id: "Mensalist",
        header: t('partner.analysts_table_monthly'),
        cell: (info) => renderTierBadge(info.getValue()),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.user?.email,
        id: "Email",
        header: "EMAIL",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.user?.phone,
        id: "Telefone",
        header: t('partner.analysts_table_phone'),
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => formatDate(row.createdAt ?? ""),
        id: "Criado em",
        header: t('partner.analysts_table_created'),
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.id,
        id: "Ações",
        enableColumnFilter: false,
        enableHiding: true,
        header: t('partner.analysts_table_actions'),
        cell: (row) => {
          return (
            <TableRowActions
              editAction={() => editAnalyst(row.getValue())}
              viewAction={() => navigate(`/assessments/analysts/${row.getValue()}`)}
              addAction={() => openTransferModal(row.getValue())}
              retrieveAction={() => openRetrieveModal(row.getValue())}
              resetAction={() => openResetPasswordDialog(row.getValue())}
            />
          );
        },
      },
    ],
    []
  );

  useEffect(() => {
    loadAnalysts();
  }, [updateData]);

  function closeModal() {
    onClose()
    setAnalyst(initialAnalyst)
    setCredits(initialCredit)
  }

  function openTransferModal(teamId: number) {
    setCredits({
      ...credits,
      to_team_id: teamId
    })
    onOpen()
    setModalType('transfer')
  }

  function openRetrieveModal(teamId: number) {
    setCredits({
      ...credits,
      from_team_id: teamId,
      to_team_id: 0
    })
    onOpen()
    setModalType('retrieve')
  }

  async function loadAnalysts() {
    setIsLoading(true);
    try {
      const response = await getAnalysts();
      setAnalysts(response.message);
      setIsLoading(false);
    } catch (error) {
      setAnalysts([]);
      setIsLoading(false);
    }
  }

  async function editAnalyst(analystId: number) {
    toast({
      title: t('general.loading'),
      status: 'loading',
      isClosable: true,
    })
    try {
      const req = await getAnalystById(analystId);

      toast.closeAll()
      setAnalyst(req.message);
      setIsLoading(false);
      setModalType("edit");
      onOpen();
    } catch (error) {
      setIsLoading(false);
    }
  }

  async function createNewAnalyst() {
    setIsLoading(true);
    setIsSaving(true);
    toast({
      title: t('general.saving'),
      status: 'loading',
      isClosable: true,
    })
    try {
      await newAnalyst(analyst)
      toast.closeAll()
      setIsLoading(false)
      setIsSaving(false)
      closeModal()
      setUpdateData(new Date().toLocaleString())
    } catch (error: any) {
      toast.closeAll();
      toast({
        title: error.message,
        status: 'error',
        isClosable: true,
      })
      setIsLoading(false);
      setIsSaving(false);
    }
  }

  async function handleCreditTransaction() {
    setIsSaving(true);
    toast({
      title: t('general.saving'),
      status: 'loading',
      isClosable: true,
    })
    try {
      await handleCredit(credits, modalType ?? "")
      toast.closeAll()
      toast({
        title: t('general.success'),
        status: 'success',
        isClosable: true,
      })
      setIsSaving(false)
      closeModal()
      setUpdateData(new Date().toLocaleString())
    } catch (error: any) {
      toast.closeAll();
      toast({
        title: t('general.error'),
        status: 'error',
        isClosable: true,
      });
      setIsSaving(false);
    }
  }

  const handleUpdateAnalyst = async (payload: ITeam) => {
    setIsSaving(true);
    try {
      toast({
        title: t('general.loading'),
        status: 'loading',
        isClosable: true,
      })
      await updateAnalyst(payload);
      setUpdateData(new Date().toLocaleString())
      setIsSaving(false);
      closeModal()
    } catch (error: any) {
      setIsSaving(false);
      console.error(error.message);
    }
  };

  const handleDeleteAnalyst = async () => {
    toast({
      title: t('general.deleting'),
      status: 'loading',
      isClosable: true,
    })
    try {
      await deleteAnalyst(analyst.id ?? 0);
      setUpdateData(new Date().toLocaleString())
      closeModal()
    } catch (error: any) {
      toast.closeAll()
      toast({
        title: t('general.error'),
        status: 'error',
        duration: 9000,
        isClosable: true,
      })
    }
  };

  async function handleChange(event: any) {
    setAnalyst({
      ...analyst,
      [event.target.name]: event.target.value,
    });
  }

  async function handleChangeCredit(event: any) {
    setCredits({
      ...credits,
      [event.target.name]: event.target.value,
    });
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
    } else {
      toast({
        title: t('general.invalid_format'),
        description: t('general.invalid_format_description'),
        status: 'error',
        isClosable: true,
      });
    }
  };

  const handleUpload = async () => {
    setIsSaving(true);
    toast({
      title: t('general.loading'),
      status: 'loading',
      isClosable: true,
    })

    let formData = new FormData();
    formData.append('file', file ?? new Blob());

    try {
      await batchAnalysts(formData);
      toast({
        title: t('general.success'),
        status: 'success',
        isClosable: true,
      });
      setFile(null);
      setIsSaving(false);
      setUpdateData(new Date().toLocaleString())
      closeModal()
    } catch (error: any) {
      setIsSaving(false);
      toast({
        title: t('general.invalid_format'),
        status: 'error',
        isClosable: true,
      });
    }
  };

  async function openResetPasswordDialog(analystId: number) {
    try {
      toast({
        title: t('general.loading'),
        status: 'loading',
        isClosable: true,
      })
      const req = await getAnalystById(analystId);
      toast.closeAll()
      setAnalyst(req.message);
      setModalType("reset_password");
      onOpen();
    } catch (error) {
      setIsLoading(false);
    }
  }

  const handleResetPassword = async () => {
    try {
      setIsSaving(true)
      await resetPassword(analyst.userId);
      setUpdateData(new Date().toLocaleString())
      setIsSaving(false)
      closeModal()
    } catch (error: any) {
      toast.closeAll()
      setIsSaving(false)
      toast({
        title: t('general.error'),
        status: 'error',
        duration: 9000,
        isClosable: true,
      })
    }
  };

  useEffect(() => {
    if (analyst?.name.trim() && (analyst?.email?.trim() || analyst?.user?.email?.trim()) && (analyst?.phone?.trim() || analyst?.user?.phone?.trim())) {
      setIsValid(true)
    } else {
      setIsValid(false)
    }
  }, [analyst])

  function RenderModal() {
    switch (modalType) {
      case "edit":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('partner.analysts_edit')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>{t('partner.analysts_new_title')}</FormLabel>
                    <ChakraInput
                      name="name"
                      placeholder={t('general.placeholder_name')}
                      type="text"
                      onChange={handleChange}
                      value={analyst?.name || ""}
                      required
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>E-mail</FormLabel>
                    <ChakraInput
                      name="email"
                      placeholder={t('general.placeholder_email')}
                      type="email"
                      onChange={(e) => setAnalyst({ ...analyst, user: { ...analyst?.user, [e.target.name]: e.target.value } })}
                      value={analyst?.user?.email || ""}
                      required
                    />
                  </FormControl>
                  <PhoneInput
                    value={analyst?.user?.phone || ""}
                    onChange={(phone: string) => setAnalyst({ ...analyst, user: { ...analyst?.user, phone } })}
                  />
                  <FormControl display='flex' alignItems='center' gap="20px">
                    <Switch isChecked={analyst.isPremium} id='isPremium' colorScheme={'blue'} onChange={(e) => setAnalyst({ ...analyst, isPremium: e.target.checked })} />
                    <FormLabel htmlFor='isPremium' mb='0'>
                      {t('partner.analysts_premium')}
                    </FormLabel>
                  </FormControl>
                </VStack>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button
                  colorScheme={"blue"}
                  onClick={() => handleUpdateAnalyst(analyst)} isLoading={isSaving} isDisabled={!isValid}>
                  {t('actions.save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case "new":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('partner.analysts_new')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>{t('partner.analysts_new_title')}</FormLabel>
                    <ChakraInput
                      name="name"
                      placeholder={t('general.placeholder_name')}
                      type="text"
                      onChange={handleChange}
                      value={analyst?.name || ""}
                      required
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>E-mail</FormLabel>
                    <ChakraInput
                      name="email"
                      placeholder={t('general.placeholder_email')}
                      type="email"
                      onChange={handleChange}
                      required
                    />
                  </FormControl>
                  <PhoneInput
                    value={analyst?.phone || ""}
                    onChange={(phone: string) => setAnalyst({ ...analyst, phone })}
                  />
                  <FormControl display='flex' alignItems='center' gap="20px">
                    <Switch id='isPremium' colorScheme={'blue'} onChange={(e) => setAnalyst({ ...analyst, isPremium: e.target.checked })} />
                    <FormLabel htmlFor='isPremium' mb='0'>
                      {t('partner.analysts_premium')}
                    </FormLabel>
                  </FormControl>
                </VStack>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button colorScheme={"blue"} onClick={createNewAnalyst} isLoading={isSaving} isDisabled={!isValid}>
                  {t('actions.save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case 'delete':
        return (
          <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={closeModal}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                  {t('partner.analysts_delete')}
                </AlertDialogHeader>

                <AlertDialogBody>
                  {t('actions.delete_confirm')}
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={closeModal}>
                    {t('actions.cancel')}
                  </Button>
                  <Button colorScheme='red' onClick={handleDeleteAnalyst} ml={3}>
                    {t('actions.delete_confirm_button')}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        )
      case 'reset_password':
        return (
          <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={closeModal}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                  {t('auth.forgot_password_title_reset')}
                </AlertDialogHeader>

                <AlertDialogBody>
                  {t('auth.user_reset_password')}
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={closeModal}>
                    {t('actions.cancel')}
                  </Button>
                  <Button isLoading={isSaving} colorScheme='red' onClick={handleResetPassword} ml={3}>
                    {t('auth.user_reset_password_button')}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        )
      case "transfer":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('actions.add_credits')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>{t('actions.add_credits_title')}</FormLabel>
                    <ChakraInput
                      name="amount"
                      placeholder="Ex: 200"
                      type="number"
                      min="0"
                      onChange={handleChangeCredit}
                      value={credits?.amount || ""}
                      required
                    />
                  </FormControl>
                </VStack>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button colorScheme={"blue"} onClick={handleCreditTransaction} isLoading={isSaving}>
                  {t('actions.save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case "retrieve":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('actions.retrieve_credits')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>{t('actions.retrieve_credits_title')}</FormLabel>
                    <ChakraInput
                      name="amount"
                      placeholder="Ex: 100"
                      type="number"
                      min="0"
                      onChange={handleChangeCredit}
                      value={credits?.amount || ""}
                      required
                    />
                  </FormControl>
                </VStack>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button colorScheme={"blue"} onClick={handleCreditTransaction} isLoading={isSaving}>
                  {t('actions.save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case "batch":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('partner.analysts_import')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>Arquivo CSV</FormLabel>
                    <ChakraInput
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      id="file-upload"
                    />
                  </FormControl>
                  <Alert status='info'>
                    <AlertIcon />
                    <Box>
                      <AlertTitle>
                        <a href="https://bemap.sfo3.cdn.digitaloceanspaces.com/bemap_import_analysts.xlsx" target="_blank">Download do template</a>
                      </AlertTitle>
                      <AlertDescription>
                        {t('partner.certificates_alert_text')}
                      </AlertDescription>
                    </Box>
                  </Alert>
                </VStack>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button colorScheme={"blue"} onClick={handleUpload} isLoading={isSaving}>
                  {t('partner.certificates_alert_save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
    }
  }

  if (isLoading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <>
      <VStack gap="20px">
        <PageHeading title={t('partner.analysts')} actions={
          [
            {
              label: t('partner.analysts_import'),
              variant: "outline",
              icon: <MdList />,
              onClick: () => {
                onOpen(), setModalType("batch");
              }
            },
            {
              label: t('partner.analysts_new'),
              icon: <AddIcon />,
              onClick: () => {
                onOpen(), setModalType("new");
              }
            }
          ]
        } />
        <Panel>
          <Table
            tableId="admin-analysts-table"
            columns={columns}
            data={analysts}
          />
        </Panel>
      </VStack>
      {RenderModal()}
    </>
  );
};

export default AnalystsPage; 